import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import classNames from "classnames/bind";

import { TData } from "../../types/TData";
import { IColumnDescription } from "../../types/TColumnDescription";
import { useUpdateProjectMutation, addMessage } from "../../store/services";
import { debounce } from "../../helpers";
import DatePicker from "../DatePicker/DatePicker.com";
import Select from "../SelectNew/Select";
import Input from "../Input/Input";
import SlideCheckbox from "../SlideCheckbox/SlideCheckbox.com";
import BasicTextarea from "../BasicTextarea/BasicTextarea.com";

import styles from "./DynamicFieldsForm.module.scss";

const cx = classNames.bind(styles);

interface Props {
  project: TData;
  editableFields: IColumnDescription[];
}

function DynamicFieldsForm({ project, editableFields }: Props) {
  const [updateProject] = useUpdateProjectMutation();
  const dispatch = useDispatch();

  // Parse API response and show appropriate message
  const handleUpdateResponse = useCallback((fieldName: string, response: any) => {
    if (response?.data && Array.isArray(response.data)) {
      const result = response.data[0];
      if (result?.status === 200) {
        dispatch(addMessage({ type: 'success', text: `${fieldName} updated successfully` }));
      } else if (result?.status === 400 && result?.errors) {
        const errorMessages = result.errors.map((err: any) => err.message).join(', ');
        dispatch(addMessage({ type: 'error', text: `Failed to update ${fieldName}: ${errorMessages}` }));
      }
    } else if (response?.error) {
      dispatch(addMessage({ type: 'error', text: `Failed to update ${fieldName}: Network error` }));
    }
  }, [dispatch]);

  // Create a debounced function that handles responses
  const debouncedUpdateProject = useMemo(() => {
    const debouncedFn = debounce((payload: any, fieldName: string) => {
      updateProject(payload)
        .then((response) => {
          handleUpdateResponse(fieldName, response);
        })
        .catch((error) => {
          console.error('Debounced update failed:', error);
          dispatch(addMessage({ type: 'error', text: `Failed to update ${fieldName}: Network error` }));
        });
    }, 500);
    return debouncedFn;
  }, [updateProject, dispatch, handleUpdateResponse]);

  // Track the current project ID to detect when we switch projects
  const currentProjectId = useRef<string | null>(null);

  // Create default values from project data
  const defaultValues = useMemo(() => {
    const values: Record<string, any> = {};
    editableFields.forEach(field => {
      const value = project[field.name as keyof TData];
      if (field.type === "Date" && value) {
        values[field.name] = new Date(value as string);
      } else if (field.type === "Checkbox") {
        values[field.name] = value || false;
      } else {
        values[field.name] = value || "";
      }
    });
    return values;
  }, [project, editableFields]);

  const { watch, setValue, reset } = useForm({
    defaultValues,
    mode: "onChange",
  });

  const watchedValues = watch();

          // Create a string representation of relevant project data for comparison
  const projectDataKey = useMemo(() => {
    return editableFields.map(field => {
      const value = project[field.name as keyof TData];
      return `${field.name}:${value}`;
    }).join('|');
  }, [project, editableFields]);

  // Reset form when project changes or project data changes
  useEffect(() => {
    // Create new form values from the project data
    const newValues: Record<string, any> = {};
    editableFields.forEach(field => {
      const value = project[field.name as keyof TData];
      if (field.type === "Date" && value) {
        newValues[field.name] = new Date(value as string);
      } else if (field.type === "Checkbox") {
        newValues[field.name] = value || false;
      } else {
        newValues[field.name] = value || "";
      }
    });

    // Reset the form with the new values
    reset(newValues);

    // Update the current project ID
    currentProjectId.current = project.Id;
  }, [project, project.Id, projectDataKey, editableFields, reset]);

      const handleFieldChange = (field: IColumnDescription, value: any) => {
    setValue(field.name, value);

    let updateValue = value;
    if (field.type === "Date" && value) {
      updateValue = (value as Date).toISOString();
    }

    // For checkbox fields, preserve boolean values including false
    // For other fields, use empty string as fallback for null/undefined
    const finalValue = field.type === "Checkbox" ? updateValue : (updateValue || "");
    const updatePayload = [{ Id: project.Id, [field.name]: finalValue }];

    if (field.type === "Text" || field.type === "Html") {
      // Use debounced update for text fields
      debouncedUpdateProject(updatePayload, field.name);
    } else {
      // Immediate update for other field types
      updateProject(updatePayload)
        .then((response) => {
          handleUpdateResponse(field.name, response);
        })
        .catch((error) => {
          console.error(`Failed to update ${field.name}:`, error);
          dispatch(addMessage({ type: 'error', text: `Failed to update ${field.name}: Network error` }));
        });
    }
  };

  const renderField = (field: IColumnDescription) => {
    const currentValue = watchedValues[field.name];

    // Check if field has choices (dropdown) regardless of type
    if (field.choices && field.choices.length > 0) {
      return (
        <div className={cx("row")} key={field.id}>
          <label htmlFor={`field-${field.id}`}>{field.name}</label>
          <Select
            placeholder={`Select ${field.name}`}
            options={field.choices.map(choice => ({
              value: choice.value,
              label: choice.text
            }))}
            onChange={(value) => handleFieldChange(field, value)}
            value={currentValue}
            isClearable
            id={`field-${field.id}`}
          />
        </div>
      );
    }

    switch (field.type) {
      case "Date":
        return (
          <div className={cx("row")} key={field.id}>
            <label htmlFor={`field-${field.id}`}>{field.name}</label>
            <DatePicker
              onChange={(value) => handleFieldChange(field, value)}
              date={currentValue}
              placeholder={field.name}
              id={`field-${field.id}`}
            />
          </div>
        );

      case "Text":
      case "Email":
      case "Phone":
      case "URL":
        return (
          <div className={cx("row")} key={field.id}>
            <label htmlFor={`field-${field.id}`}>{field.name}</label>
            <Input
              name={`field-${field.id}`}
              onChange={(e) => handleFieldChange(field, e.currentTarget.value)}
              value={currentValue || ""}
              placeholder={`Enter ${field.name}`}
            />
          </div>
        );

      case "Html":
        return (
          <div className={cx("row")} key={field.id}>
            <BasicTextarea
              label={field.name}
              onChange={(value) => handleFieldChange(field, value)}
              value={currentValue || ""}
              placeholder={`Enter ${field.name}`}
            />
          </div>
        );

      case "Checkbox":
        return (
          <div className={cx("row", "toggle")} key={field.id}>
            <SlideCheckbox
              checked={currentValue || false}
              onChange={(value) => handleFieldChange(field, value)}
              renderLabel={() => field.name}
            />
          </div>
        );

      case "Number":
      case "Numeric":
      case "Currency":
      case "Percent":
        return (
          <div className={cx("row")} key={field.id}>
            <label htmlFor={`field-${field.id}`}>{field.name}</label>
            <Input
              type="number"
              name={`field-${field.id}`}
              onChange={(e) => handleFieldChange(field, parseFloat(e.currentTarget.value) || 0)}
              value={currentValue || ""}
              placeholder={`Enter ${field.name}`}
            />
          </div>
        );

      default:
        // Default to text input for unknown types
        return (
          <div className={cx("row")} key={field.id}>
            <label htmlFor={`field-${field.id}`}>{field.name}</label>
            <Input
              name={`field-${field.id}`}
              onChange={(e) => handleFieldChange(field, e.currentTarget.value)}
              value={currentValue || ""}
              placeholder={`Enter ${field.name}`}
            />
          </div>
        );
    }
  };

  return (
    <form className={cx("dynamic-form")}>
      {editableFields.map(field => renderField(field))}
    </form>
  );
}

export default DynamicFieldsForm;

// Ensure this file is treated as a module